import React, { useState, useEffect, useRef } from "react";
import { cachedFetch } from "../utils/cached-api-wrapper";
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import { dropdownWidthStyle } from '../utils/dropdown-width';

// MUI pins the dropdown to the anchor's width — Autocomplete renders the popper
// with an inline `style={{ width: anchorEl.clientWidth }}`, which no stylesheet
// rule can override. In a narrow table column that forces long options to wrap
// over several lines; a matter carrying its client name (XPEN-3323) reads as
// three cramped rows instead of one.
//
// `minWidth` has to come from the inline style MUI computed: the popper is
// absolutely positioned, so a percentage would resolve against its containing
// block (the body, once portalled) and not against the input.
//
// Rendered via Autocomplete's `as` prop, so it still receives MUI's own popper
// className (z-index, and `position: absolute` on the disablePortal mounts):
// forward the ref and spread everything through.
//
// XPEN-3467: the sizing itself lives in `utils/dropdown-width`, shared with
// react-select's menu and with currency-select, the app's other MUI
// Autocomplete, so the two libraries can't drift apart.
//
// Exported for the unit test: jsdom's CSS parser silently drops
// `width: max-content`, so the computed style is asserted here rather than
// read back off the rendered node.
export const WideDropdownPopper = React.forwardRef(function WideDropdownPopper({ style, ...props }, ref) {
  return (
    <Popper
      {...props}
      ref={ref}
      placement="bottom-start"
      style={{ ...style, ...dropdownWidthStyle(style?.width) }}
    />
  );
});

const assignRef = (ref, value) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};

const defaultSuggestionLabel = (suggestion) => {
  if (typeof suggestion === "string") return suggestion;
  const name = suggestion?.name ?? "";
  const description = suggestion?.description ?? "";
  return `${name}${description ? ` (${description})` : ""}`;
};

export default function AutoCompleteField({
  data,
  value,
  onChange,  // onChange(value)
  searchUrl, // url to search for suggestions
  placeholder,
  className,
  required = false,
  max_results = 10,
  minLength = 3,
  dropup = false,
  onParentBlur = () => {},
  "data-testid": dataTestId,
  disabled = false,
  disablePortal = false,
  formatLabel,
  // XPEN-3322: stored value <-> editable text. `formatEditValue` renders the
  // stored value the way the user reads it (matters drop the always-"000"
  // sub-client segment); `parseEditValue` turns typed text back into the value
  // to store, resolved against the suggestions already fetched. Identity by
  // default, so every non-matter mount behaves exactly as before.
  formatEditValue = (storedValue) => storedValue,
  parseEditValue = (text) => text,
}) {
  const getSuggestionLabel = formatLabel || defaultSuggestionLabel;
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? "");
  const [displayValue, setDisplayValue] = useState(value ?? "");
  const [wasModified, setWasModified] = useState(false);
  const [hasFetchedSuggestions, setHasFetchedSuggestions] = useState(false);
  const validationRef = useRef(null);
  const skipNextBlurSaveRef = useRef(false);
  // The last list the search returned. `handleSelect` clears the `suggestions`
  // state before blur runs, so the ref — written only by the fetch — is what
  // `parseEditValue` can still resolve against on the way out.
  const suggestionsRef = useRef([]);
  // The full identifier of an explicitly picked suggestion, so a pick is never
  // re-derived from the collapsed text sitting in the input.
  const selectedValueRef = useRef(null);
  // Held in a ref so the value-sync effects below don't take a prop that is a
  // fresh arrow on every render as a dependency.
  const formatEditValueRef = useRef(formatEditValue);
  formatEditValueRef.current = formatEditValue;
  const t = (key, fallback) => {
    if (data?.t?.read) return data.t.read(key);
    return fallback;
  };

  // Keep local value in sync with parent value and reset modified state once parent accepts a save.
  useEffect(() => {
    setLocalValue(formatEditValueRef.current(value ?? ""));
    setDisplayValue(formatEditValueRef.current(value ?? ""));
    setWasModified(false);
    selectedValueRef.current = null;
  }, [value]);

  useEffect(() => {
    const searchTerm = String(value ?? "").trim();

    if (!searchUrl || !searchTerm) {
      setDisplayValue(formatEditValueRef.current(value ?? ""));
      return;
    }

    let cancelled = false;

    cachedFetch(
      `${searchUrl}?text=${encodeURIComponent(searchTerm)}`,
      "GET",
      {},
      (response) => {
        if (cancelled) return;

        const list = Array.isArray(response?.data) ? response.data : [];
        const found = list.find((option) => {
          if (typeof option === "string") return option === searchTerm;
          return option?.name === searchTerm || option?.altNumber === searchTerm
            || option?.value === searchTerm || option?.unit === searchTerm;
        });

        // XPEN-3094: the admin's matter_display config wins — a MatterId stored
        // as its altNumber (via XPEN-3080 extraction) still hydrates through the
        // config-driven formatter, so BCF's "number-only" setting flips the
        // display to the canonical number regardless of what was extracted.
        // XPEN-3322: the no-hit fallback prints the stored value, so it goes
        // through the same formatter — otherwise a matter that fails to resolve
        // (inactive, excluded, past the result cap) shows its full number while
        // every matter around it shows the collapsed one.
        setDisplayValue(found ? getSuggestionLabel(found) : formatEditValueRef.current(value ?? ""));
      },
      () => {
        if (!cancelled) setDisplayValue(formatEditValueRef.current(value ?? ""));
      },
      false,
      null,
      { cacheType: 'autocomplete' }
    );

    return () => {
      cancelled = true;
    };
  }, [value, searchUrl]);

  // Fetch suggestions
  useEffect(() => {
    if (!isOpen || !localValue || localValue.length < minLength) {
      setSuggestions([]);
      setHasFetchedSuggestions(false);
      return;
    }

    const searchTerm = localValue.trim();
    if (!searchTerm) return;

    cachedFetch(
      `${searchUrl}?text=${encodeURIComponent(searchTerm)}`,
      "GET",
      {},
      (response) => {
        // Keep only array responses to prevent rendering invalid suggestion states.
        const list = Array.isArray(response?.data) ? response.data : [];
        setSuggestions(list);
        suggestionsRef.current = list;
        setHasFetchedSuggestions(true);
      },
      () => {
        setSuggestions([]);
        suggestionsRef.current = [];
        setHasFetchedSuggestions(true);
      },
      false,
      null,
      { cacheType: 'autocomplete' }
    );
  }, [localValue, searchUrl, minLength, isOpen]);

  const handleSelect = (suggestion) => {
    // A suggestion selection is an explicit save action, so blur must not save again.
    skipNextBlurSaveRef.current = true;
    setSuggestions([]);
    setIsOpen(false);
    setIsSelected(false);
    selectedValueRef.current = suggestion?.name || suggestion;
    setLocalValue(formatEditValue(selectedValueRef.current));
    setWasModified(false);
    onChange(suggestion);
  };

  const handleChange = (newValue) => {
    selectedValueRef.current = null;
    setLocalValue(newValue); // Update local state immediately for responsive UI
    setWasModified(true);
    setHasFetchedSuggestions(false);
    setIsOpen(true);
  };

  // What the parent must receive: always a value it can store, never the
  // collapsed text the input shows. An explicit pick wins; an untouched field
  // hands back exactly what was stored (no round-trip through the formatter, so
  // no race with the suggestion fetch); typed text is parsed back.
  const resolveOutgoingValue = () => {
    if (selectedValueRef.current != null) return selectedValueRef.current;
    if (!wasModified) return value ?? "";
    return parseEditValue(localValue ?? "", suggestionsRef.current);
  };

  const saveIfNeeded = () => {
    const nextValue = resolveOutgoingValue();
    const previousValue = value ?? "";

    // Match FormTextfield save behavior: save only if the field was edited and value changed.
    if (!disabled && isSelected && wasModified && nextValue + "" !== previousValue + "") {
      onChange(nextValue);
      setWasModified(false);
    }
  };

  const shouldShowDropdown = isOpen && localValue?.trim()?.length >= minLength && (suggestions.length > 0 || hasFetchedSuggestions);

  return (
      
        <Autocomplete
            id={dataTestId || "free-solo-demo"}
            freeSolo
            disablePortal={disablePortal}
            PopperComponent={WideDropdownPopper}
            options={suggestions.slice(0, max_results)}
            inputValue={isSelected ? localValue : displayValue}
            disabled={disabled}
            open={shouldShowDropdown}
            noOptionsText={t("no_results_found", "No results found")}
            getOptionLabel={getSuggestionLabel}
            isOptionEqualToValue={(option, selectedValue) => {
              return getSuggestionLabel(option) === getSuggestionLabel(selectedValue);
            }}
            onInputChange={(event, newValue, reason) => {
              if (reason === "input" || reason === "clear") {
                handleChange(newValue);
              }
            }}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                handleSelect(newValue);
              }
            }}
            onFocus={() => {
              setIsOpen(true);
              setIsSelected(true);
            }}
            onBlur={() => {
              // Not `localValue`: the mileage matter field (expense.js) has a
              // no-op onChange and writes MatterId straight from this argument.
              onParentBlur(resolveOutgoingValue());
              setIsOpen(false);

              if (skipNextBlurSaveRef.current) {
                skipNextBlurSaveRef.current = false;
                setIsSelected(false);
                return;
              }

              saveIfNeeded();
              setIsSelected(false);
            }}
            renderOption={(props, option) => (
                <li {...props} className="dropdown-item text-wrap">
                  {getSuggestionLabel(option)}
                  {option?.code && (
                      <span className="text-muted ml-1">({option.code})</span>
                  )}
                </li>
            )}
            renderInput={(params) => {
              const { inputProps, InputProps } = params;
              const { ref: muiInputRef, ...restInputProps } = inputProps;

              return (
                  <div ref={InputProps.ref} className="w-100">
                    <input
                        {...restInputProps}
                        ref={(node) => {
                          assignRef(muiInputRef, node);
                          validationRef.current = node;
                        }}
                        required={required}
                        placeholder={placeholder}
                        type="text"
                        className={className}
                        data-testid={dataTestId}
                        disabled={disabled}
                    />
                  </div>
              );
            }}
        />
  
  );
}
